export const READ_TIMEOUT_MS = 12_000;

export class ReadTimeoutError extends Error {
  constructor() {
    super('The server is taking too long to respond. Check your connection and try again.');
    this.name = 'ReadTimeoutError';
  }
}

export function withReadTimeout<T>(request: Promise<T>, timeoutMs = READ_TIMEOUT_MS): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new ReadTimeoutError()), timeoutMs);
    request.then(resolve, reject).finally(() => clearTimeout(timer));
  });
}

// Keep a slow listener alive after reporting its deadline, so a later valid
// snapshot can recover the screen. Unmounted or replaced listeners are ignored.
export function subscribeWithDeadline<T>(
  subscribe: (next: (value: T) => void, fail: (error: Error) => void) => () => void,
  onNext: (value: T) => void,
  onError: (error: Error) => void,
  timeoutMs = READ_TIMEOUT_MS
): () => void {
  let active = true;
  const timer = setTimeout(() => { if (active) onError(new ReadTimeoutError()); }, timeoutMs);
  const unsubscribe = subscribe(
    (value) => {
      clearTimeout(timer);
      if (active) onNext(value);
    },
    (error) => {
      clearTimeout(timer);
      if (active) onError(error);
    }
  );
  return () => {
    if (!active) return;
    active = false;
    clearTimeout(timer);
    unsubscribe();
  };
}
